import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState, useMemo } from 'react';
import SearchInput from './SearchInput';
import ResultsList from './ResultsList';
import NoResults from './NoResults';
import { useSearchEngine } from '../hooks/useSearchEngine';
import { useKeyboardNav } from '../hooks/useKeyboardNav';
import { getI18n } from '../lib/i18n';
const SearchModal = ({ isOpen, onClose, products, synonyms, indexLoaded, error, }) => {
    const i18n = getI18n();
    const modalRef = useRef(null);
    const [query, setQuery] = useState('');
    const [includeBStock, setIncludeBStock] = useState(false);
    const enabledFields = window.enoSearchConfig?.enabledFields;
    const maxResults = window.enoSearchConfig?.maxResults;
    const salesBoost = window.enoSearchConfig?.salesBoost;
    const searchEngine = useSearchEngine(products, synonyms, enabledFields, maxResults, salesBoost);
    // Search results — exclude b-stock products unless user opts in
    const results = useMemo(() => {
        if (!query.trim() || !searchEngine.isReady)
            return [];
        const raw = searchEngine.search(query);
        if (includeBStock)
            return raw;
        return raw.filter(r => !r.product.sku.toUpperCase().startsWith('BST-'));
    }, [query, searchEngine, includeBStock]);
    // Keyboard navigation
    const handleSelect = (result) => {
        window.location.href = result.product.url;
    };
    const { activeIndex, setActiveIndex } = useKeyboardNav(results, handleSelect, onClose);
    // Close on backdrop click
    useEffect(() => {
        const handleBackdropClick = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleBackdropClick);
        }
        return () => document.removeEventListener('mousedown', handleBackdropClick);
    }, [isOpen, onClose]);
    // Focus trap and auto-focus input
    useEffect(() => {
        if (!isOpen)
            return;
        // Auto-focus search input
        const timer = setTimeout(() => {
            const input = modalRef.current?.querySelector('input');
            input?.focus();
        }, 100);
        // Tab key trap
        const handleTabKey = (e) => {
            if (e.key !== 'Tab' || !modalRef.current)
                return;
            const focusableElements = modalRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focusableElements.length === 0)
                return;
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (e.shiftKey && document.activeElement === firstElement) {
                lastElement.focus();
                e.preventDefault();
            }
            else if (!e.shiftKey && document.activeElement === lastElement) {
                firstElement.focus();
                e.preventDefault();
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('keydown', handleTabKey);
        };
    }, [isOpen]);
    // Reset state when opening
    useEffect(() => {
        if (isOpen) {
            setQuery('');
            setActiveIndex(0);
        }
    }, [isOpen, setActiveIndex]);
    // Listen for suggestion clicks from NoResults component
    useEffect(() => {
        const handleSuggestion = (e) => {
            setQuery(e.detail);
        };
        window.addEventListener('eno-search:suggestion', handleSuggestion);
        return () => window.removeEventListener('eno-search:suggestion', handleSuggestion);
    }, []);
    if (!isOpen)
        return null;
    const resultCount = results.length;
    const resultLabel = resultCount === 1 ? i18n.resultSingular : i18n.resultPlural;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: `eno-search-backdrop ${isOpen ? 'open' : ''}` }), _jsx("div", { className: `eno-search-modal-container ${isOpen ? 'open' : ''}`, children: _jsxs("div", { className: "eno-search-modal", ref: modalRef, role: "dialog", "aria-modal": "true", "aria-label": i18n.searchLabel, tabIndex: -1, children: [_jsxs("div", { className: "eno-search-modal-header", children: [_jsx("h2", { children: i18n.modalTitle }), _jsx("p", { children: i18n.modalSubtitle }), _jsx("button", { className: "eno-search-modal-close", onClick: onClose, "aria-label": i18n.closeSearch, children: "\u2715" })] }), _jsx("div", { className: "eno-search-modal-body", children: error ? (_jsxs("div", { className: "eno-search-error", children: [_jsx("h3", { children: i18n.errorTitle }), _jsx("p", { children: error })] })) : !indexLoaded ? (_jsx("div", { className: "eno-search-loading", children: i18n.loadingIndex })) : (_jsxs(_Fragment, { children: [_jsx(SearchInput, { value: query, onChange: setQuery, onClear: () => setQuery(''), autoFocus: true }), query.trim() === '' ? (_jsxs("div", { className: "eno-search-no-results", children: [_jsx("h3", { children: i18n.startTyping }), _jsx("p", { children: i18n.startTypingDesc })] })) : results.length === 0 ? (_jsx(NoResults, { query: query, synonyms: synonyms })) : (_jsx(ResultsList, { results: results, activeIndex: activeIndex, onSelect: handleSelect }))] })) }), _jsxs("div", { className: "eno-search-modal-footer", children: [_jsxs("label", { className: "eno-search-b-stock-toggle", children: [_jsx("input", { type: "checkbox", checked: includeBStock, onChange: e => setIncludeBStock(e.target.checked) }), _jsx("span", { children: i18n.includeBStock })] }), _jsxs("span", { className: "eno-search-footer-count", children: [resultCount, " ", resultLabel] })] })] }) })] }));
};
export default SearchModal;
